/**
 * A free-trial invite waiting for its coach to finish signing up (2026-10-25). The invite page (AFSStart.jsx) keeps
 * the token here, so a coach who confirms their email in a new tab, or signs in later, still lands back on the
 * invite instead of the platform's paywall (BAMFull.jsx). Cleared once the trial starts, or after 14 days.
 */
import { AFS_PLANS, isOfferId } from "./afsPlans";

const KEY = "bam_afs_invite";
const TTL = 14 * 86400_000;

export function keepInvite(token) {
  try { localStorage.setItem(KEY, JSON.stringify({ t: token, at: Date.now() })); } catch { /* private window */ }
}
export function clearInvite() {
  try { localStorage.removeItem(KEY); } catch { /* private window */ }
}
/** The waiting invite's token, or null. */
export function pendingInvite() {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) || "null");
    if (!v?.t || !/^[A-Za-z0-9_-]{24,64}$/.test(v.t) || !(Date.now() - v.at < TTL)) return null;
    return v.t;
  } catch { return null; }
}

/** The text to send a coach with their link. */
export const inviteMessage = (url, games) => `Here's your free Advantage Flow trial (${games} ${games === 1 ? "game" : "games"}): ${url}
Open it on a computer in Chrome, create your account, name your team, and you're in. The tour shows you around. Before you tag, read What to say in the game screen: saying every attempt, every pass and every shot is what makes the numbers right. Start with one quarter, and tell me how it goes.`;

// A tagger's invite (2026-10-25), kept the same way: signing up through it never lands on the coaches' paywall.
const TAGGER_KEY = "bam_afs_tagger_invite";
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export function keepTaggerInvite(id) {
  try { localStorage.setItem(TAGGER_KEY, JSON.stringify({ t: id, at: Date.now() })); } catch { /* private window */ }
}
export function clearTaggerInvite() {
  try { localStorage.removeItem(TAGGER_KEY); } catch { /* private window */ }
}
export function pendingTaggerInvite() {
  try {
    const v = JSON.parse(localStorage.getItem(TAGGER_KEY) || "null");
    return v?.t && UUID.test(v.t) && Date.now() - v.at < TTL ? v.t : null;
  } catch { return null; }
}

// A plan or a done-for-you offer picked before the account existed (2026-11-03 sweep): a buyer who has to confirm their
// email comes back to the checkout for it, not the coaches' $34.99 paywall. Cleared once the payment is confirmed.
const BUY_KEY = "bam_afs_buy";
export function keepPurchase({ plan = null, offer = null } = {}) {
  if (!plan && !offer) return;
  try { localStorage.setItem(BUY_KEY, JSON.stringify({ plan, offer, at: Date.now() })); } catch { /* private window */ }
}
export function clearPurchase() {
  try { localStorage.removeItem(BUY_KEY); } catch { /* private window */ }
}
/** The hash to send them back to (#/afs-start?plan=... or ?offer=...), or null. */
export function pendingPurchaseHash() {
  try {
    const v = JSON.parse(localStorage.getItem(BUY_KEY) || "null");
    if (!v || !(Date.now() - v.at < 3 * 86400_000)) return null;   // 3 days: long enough to confirm, not a standing detour
    if (v.offer && isOfferId(v.offer)) return `#/afs-start?offer=${v.offer}`;
    if (v.plan && AFS_PLANS[v.plan]) return `#/afs-start?plan=${v.plan}`;
    return null;
  } catch { return null; }
}

// A staff invite (2026-11-03): a team admin added this address before it had an account (api/afs-team.js). Kept the
// same way, so signing up or confirming the email in another tab comes back to the invite, and on to the team's
// Advantage Flow, never the coaches' paywall. Cleared once it's accepted, or after 14 days (the invite's own life).
const STAFF_KEY = "bam_afs_staff_invite";
export function keepStaffInvite(token) {
  try { localStorage.setItem(STAFF_KEY, JSON.stringify({ t: token, at: Date.now() })); } catch { /* private window */ }
}
export function clearStaffInvite() {
  try { localStorage.removeItem(STAFF_KEY); } catch { /* private window */ }
}
/** The waiting staff invite's token, or null. */
export function pendingStaffInvite() {
  try {
    const v = JSON.parse(localStorage.getItem(STAFF_KEY) || "null");
    return v?.t && /^[A-Za-z0-9_-]{24,64}$/.test(v.t) && Date.now() - v.at < TTL ? v.t : null;
  } catch { return null; }
}
